import { useIntl } from 'react-intl';
import { useLazyLoadQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import type { AgentMcpMetadataQuery } from '#generated/graphql/AgentMcpMetadataQuery.graphql';
import type { AgentUnavailableError } from '#definitions/errors';
import { useToken } from '../../../Flow/ConsentContextProvider';
import { headlineMessages } from '../../shared/agent-consent/Headline/messages';

const query = graphql`
  query AgentMcpMetadataQuery($agentAri: ID!) {
    agentStudio_agentById(id: $agentAri) @optIn(to: "AgentStudio") {
      __typename
      ... on AgentStudioAssistant {
        id
        name
        description
        identityAccount {
          picture
        }
      }
      ... on AgentStudioServiceAgent {
        id
        name
        description
      }
      ... on QueryError {
        message
      }
    }
  }
`;

export type AgentMcpDisplayMetadata = {
  name: string;
  description?: string;
  icon?: string;
};

type AgentResult = AgentMcpMetadataQuery['response']['agentStudio_agentById'];

/** Shown by the consent error page when the agent cannot be loaded. */
export function agentMcpMetadataError(
  clientName: string | null,
): AgentUnavailableError {
  return {
    error: 'agent-unavailable',
    clientName: clientName ?? undefined,
  };
}

function toDisplayMetadata(
  result: AgentResult,
  fallbackName: string,
): AgentMcpDisplayMetadata | null {
  if (
    result?.__typename !== 'AgentStudioAssistant' &&
    result?.__typename !== 'AgentStudioServiceAgent'
  ) {
    return null;
  }
  const name = result.name?.trim() || fallbackName;
  const description = result.description?.trim();
  const icon =
    result.__typename === 'AgentStudioAssistant'
      ? result.identityAccount?.picture?.trim()
      : undefined;
  return {
    name,
    ...(description ? { description } : {}),
    ...(icon ? { icon } : {}),
  };
}

/**
 * Display metadata for the Rovo Agent named by the signed `resources` claim.
 * Suspends while loading. A result that is not an agent throws the consent
 * error page; an agent without a name is shown as "Custom Agent".
 */
export function useAgentMcpMetadata(agentAri: string): AgentMcpDisplayMetadata {
  const { clientName } = useToken();
  const intl = useIntl();
  const data = useLazyLoadQuery<AgentMcpMetadataQuery>(
    query,
    { agentAri },
    { fetchKey: agentAri },
  );
  const metadata = toDisplayMetadata(
    data.agentStudio_agentById,
    intl.formatMessage(headlineMessages.customAgentName),
  );
  if (!metadata) {
    throw agentMcpMetadataError(clientName);
  }
  return metadata;
}
